import Cookies from 'js-cookie';
import { STORAGE_KEYS, type StorageKey } from '@/shared/constants/storage.constants';
import { XELACORE_DOMAIN, USER_REFRESH_COOKIE, USER_COOKIE, EXPIRATION_TIME } from '@/config';

export interface AuthStorageData {
  org_access_token?: string;
  name?: string;
  rememberMe?: boolean;
  selected_org_id?: string;
  user_refresh_token?: string;
}

export function setAuthStorage(data: AuthStorageData): void {
  const { org_access_token, name, rememberMe, selected_org_id, user_refresh_token } =
    data;
  const storageToUse = rememberMe ? localStorage : sessionStorage;

  // Clear all previous possible storages
  eraseAuthStorage();

  storageToUse.setItem(STORAGE_KEYS.ORG_ACCESS_TOKEN, org_access_token || '');
  storageToUse.setItem(STORAGE_KEYS.SELECTED_ORG_ID, selected_org_id || '');
  storageToUse.setItem(STORAGE_KEYS.USER_REFRESH_COOKIE, user_refresh_token || '');
  storageToUse.setItem(STORAGE_KEYS.NAME, name || '');
  storageToUse.setItem(STORAGE_KEYS.DATE, Date.now().toString());
}

export function updateAccessToken({
  org_access_token,
  rememberMe = false,
}: {
  org_access_token: string;
  rememberMe?: boolean;
}): void {
  const storageToUse = rememberMe ? localStorage : sessionStorage;
  storageToUse.setItem(STORAGE_KEYS.ORG_ACCESS_TOKEN, org_access_token || '');
  storageToUse.setItem(STORAGE_KEYS.DATE, Date.now().toString());
}

export function getItem(key: StorageKey): string | null {
  return sessionStorage.getItem(key) || localStorage.getItem(key);
}

export function getAuthStorage(): AuthStorageData | null {
  const writeDate = getItem(STORAGE_KEYS.DATE);
  if (!writeDate) return null;

  const dateDifference = Date.now() - parseInt(writeDate, 10);
  if (dateDifference > EXPIRATION_TIME) {
    return null;
  }

  return {
    name: getItem(STORAGE_KEYS.NAME) || undefined,
    org_access_token: getItem(STORAGE_KEYS.ORG_ACCESS_TOKEN) || undefined,
    user_refresh_token: getItem(STORAGE_KEYS.USER_REFRESH_COOKIE) || undefined,
    selected_org_id: getItem(STORAGE_KEYS.SELECTED_ORG_ID) || undefined,
  };
}

export function eraseAuthStorage(): void {
  [localStorage, sessionStorage].forEach((storage) => {
    Object.values(STORAGE_KEYS).forEach((key) => {
      storage.removeItem(key);
    });
  });
}

export function clearUserCookies(): void {
  Cookies.remove(USER_REFRESH_COOKIE, { domain: XELACORE_DOMAIN });
  Cookies.remove(USER_COOKIE, { domain: XELACORE_DOMAIN });
  eraseAuthStorage();
}

export function getUserCookies(): { userToken?: string; userRefreshToken?: string } {
  return {
    userToken: Cookies.get(USER_COOKIE),
    userRefreshToken: Cookies.get(USER_REFRESH_COOKIE),
  };
}
